/**
 * Copyright (c) Qwoach LLC 2025. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Artem Bogush <artem@qwoach.com> on 26.06.25
 *
 */

import {CURRENCY_FORMATTING_OPTIONS_SHORT, currencyFormatted} from "@shared/Profile/CurrencyItem";
import {ensureArray, pluralize} from "@/utils/helpers";
import BaseIdObject from "@shared/BaseIdObject";
import {Type} from "@qwoach/class-transformer-wrapper";
import {classToClass} from "@qwoach/class-transformer-wrapper";
import {
	ServiceGroupNameEnum,
	ServiceType
} from "@shared/Quote/ServiceType";
import {ServiceUsageType} from "@shared/Invoice/ServiceUsageType";
import {LineItemBreakdownMode} from "@shared/Invoice/LineItemBreakdownMode";

export default class LineItem extends BaseIdObject {

	title: string = null
	groupName: string = null
	serviceType: ServiceType = null
	description: string = null

	currencyCode: string = null

	@Type(() => LineItem)
	subLineItems: LineItem[] = []
	parentId: string = null // if this is a sub-item, the id of the parent item

	serviceUsageType: ServiceUsageType = null

	quantityMax: number = 0

	quantityNumber: number = 1
	quantityNumberUnits: string = "" // e.g. "sessions", "hours", "minutes"

	quantity: number = 0
	quantityUnits: string = "" // e.g. "min", "hr", "session", "sessions", "hours", "minutes"
	quantityTotalLogged: number = 0
	amountTotalLoggedCents: number = 0

	itemPriceCents: number = 0 // Price in cents to avoid floating point issues
	itemPriceUnits: string = "" // e.g. "per hour", "per session", "per minute"

	conversionFactor: number = 1 // Conversion factor for unit price, e.g. if itemPrice is per hour, conversion can be 60 for per minute

	serviceDateMs: number = null // Timestamp in milliseconds when this work was done.
	sourceServiceId: string = null
	sourceSessionEntryId: string = null
	scheduleSyncOverrides: string[] = []

	_draftLineItemIds: string[] = [] // Runtime-only link to original draft staging rows so grouped invoice items can clear them on submit.

	//the below props are only used for invoice rendering. Not saved to DB
	_titleFull: String = null
	_breakDownFormatted: String = null
	_totalPrice: string = null
	_itemPrice: string = null
	_commissionDetailsRef = null
	_invoiceItemRef = null
	_allInvoiceItemsRef = []


	get itemPrice() {
		return this.itemPriceCents / 100;
	}

	set itemPrice(value) {
		this.itemPriceCents = Math.round((value || 0) * 100)
	}

	constructor(options = {}) {
		super(options);
		let {subLineItems, currencyCode, ...rest} = options;

		this.applyOptions(rest);

		if(subLineItems) {
			this.addSubItems(subLineItems);
		}
		if(currencyCode){
			this.setCurrencyCode(currencyCode);
		}
	}

	setCurrencyCode(currencyCode) {
		this.currencyCode = currencyCode;
		this.subLineItems.forEach(lineItem => lineItem.setCurrencyCode(currencyCode));
	}

	setDateIfNeeded(dateMs) {
		if (!this.serviceDateMs) {
			this.serviceDateMs = dateMs;
			this.subLineItems.forEach(lineItem => lineItem.setDateIfNeeded(dateMs));
		}
	}

	addSubItems(subLineItems) {
		subLineItems = ensureArray(subLineItems);
		for(let subItem of subLineItems){
			subItem.parentId = this.id;
		}
		this.subLineItems.push(...subLineItems);
	}

	hasSubItems(){
		return this.subLineItems.length > 0;
	}
	hasSubItemsNonZeroPrice(){
		return this.getTotalPriceCentsSubLineItems() > 0;
	}
	getOwnUsageSummarySubItems() {
		return (this.subLineItems || [])
			.filter(lineItem => lineItem?.parentId === this.id)
			.filter(lineItem => !lineItem?.title)
	}
	hasMultipleOwnUsageSummarySubItems() {
		return this.getOwnUsageSummarySubItems().length > 1
	}
	getSummaryChildTitles() {
		return (this.subLineItems || [])
			.filter(lineItem => lineItem?.parentId === this.id)
			.map(lineItem => String(lineItem?.title || "").trim())
			.filter(title => !!title)
	}
	formatSummaryChildTitles() {
		let childTitles = [...new Set(this.getSummaryChildTitles())]
		if (childTitles.length === 0) {
			return ""
		}
		if (childTitles.length === 1) {
			return childTitles[0]
		}
		let leadingTitles = childTitles.slice(0, -1).join(", ")
		let trailingTitle = childTitles[childTitles.length - 1]
		return `${leadingTitles} and ${trailingTitle}`
	}

	titleWithNote() {
		return this.description ? `${this.title} - ${this.description}` : this.title;
	}

	computeValues(commissionDetails) {
		this.computeCommissionValues(commissionDetails);
		this._titleFull = this.titleWithNote();
		this._totalPrice = this.getTotalPriceFormatted()
		this._itemPrice = this.getItemPriceFormatted()
		this._breakDownFormatted = this.breakdownFormatted();
	}

	computeCommissionValues(commissionDetails) {
		if (!commissionDetails) {
			return;
		}

		this._commissionDetailsRef = commissionDetails
		this.quantityTotalLogged = this.getOwnLoggedTotal(commissionDetails, serviceLogItem => serviceLogItem.usedQuantity)
		this.amountTotalLoggedCents = this.getActualAmountCents()
	}

	getOwnLoggedTotal(commissionDetails, mapper = serviceLogItem => serviceLogItem.usedQuantity) {
		let ownLogItems = this.getMatchingServiceLogItems(commissionDetails)
		return ownLogItems.reduce((total, serviceLogItem) => total + (Number(mapper(serviceLogItem)) || 0), 0)
	}

	isMatchingServiceLogItem(serviceLogItem) {
		let sourceServiceId = this.sourceServiceId
		if (sourceServiceId) {
			return serviceLogItem.filterParams?.sourceServiceId === sourceServiceId
		}
		return serviceLogItem.serviceType === this.serviceType
	}

	getMatchingServiceLogItems(commissionDetails) {
		let matchingItems = []
		let allLogItems = [
			...commissionDetails.serviceLogArray.map(item => ({...item, _isDraft: false})),
			...commissionDetails.serviceLogArrayDraft.map(item => ({...item, _isDraft: true})),
		]
		for (let serviceLogItem of allLogItems) {
			let isMatchingService = this.isMatchingServiceLogItem(serviceLogItem)
			let isWithinDate = !serviceLogItem._isDraft || serviceLogItem.serviceDateMs <= this.serviceDateMs
			if (isMatchingService && isWithinDate) {
				matchingItems.push(serviceLogItem)
			}
		}
		return matchingItems
	}

	getTotalPrice() {
		return this.getTotalPriceCents() / 100;
	}
	getTotalPriceCents() {
		return this.getLineItemPriceCents() + this.getTotalPriceCentsSubLineItems()
	}
	getLineItemPrice() {
		return this.getLineItemPriceCents() / 100;
	}
	getLineItemPriceCents() {
		if(this.isUsageByPrice() || this.isUsageRate()){
			return this.itemPriceCents;
		}
		return this.quantityNumber * this.quantity * this.itemPriceCents * this.conversionFactor
	}
	getTotalPriceCentsSubLineItems() {
		return this.subLineItems.reduce((acc, lineItem) => acc + lineItem.getTotalPriceCents(), 0)
	}

	getTotalPriceFormatted({currencyCode = this.currencyCode, totalPrice = this.getTotalPrice()} = {}) {
		return currencyFormatted(currencyCode, totalPrice, CURRENCY_FORMATTING_OPTIONS_SHORT);
	}

	getLineItemPriceFormatted(currencyCode = this.currencyCode) {
		return currencyFormatted(currencyCode, this.getLineItemPrice(), CURRENCY_FORMATTING_OPTIONS_SHORT);
	}

	breakdownFormatted(currencyCode = this.currencyCode, options = {}) {
		let {
			mode = LineItemBreakdownMode.DEFAULT,
		} = options
		switch (mode) {
			case LineItemBreakdownMode.SUB_ITEMS_ONLY:
				return this.breakdownFormattedSubLineItems(currencyCode, {
					includeLeadingBreak: false,
				})
			case LineItemBreakdownMode.HISTORY_COMPACT:
				return this.breakdownFormattedHistoryCompact(currencyCode)
			case LineItemBreakdownMode.SUMMARY_COMPACT:
				return this.breakdownFormattedSummaryCompact(currencyCode)
			case LineItemBreakdownMode.QUOTE_PDF:
				return this.breakdownFormattedQuotePdf(currencyCode)
			case LineItemBreakdownMode.GENERAL:
				return this.breakdownFormattedGeneral(currencyCode)
			case LineItemBreakdownMode.DEFAULT:
			default:
				return this.breakdownFormattedDefault(currencyCode)
		}
	}
	breakdownFormattedDefault(currencyCode = this.currencyCode) {
		switch (this.serviceUsageType) {
			case ServiceUsageType.RATE:
			case ServiceUsageType.PRICE:
				return this.breakdownFormattedPiceUsage(currencyCode)
			case ServiceUsageType.QUANTITY:
			case ServiceUsageType.QUANTITY_NUMBER:
			default:
				return this.breakdownFormattedGeneral(currencyCode)
		}
	}
	breakdownFormattedHistoryCompact(currencyCode = this.currencyCode) {
		if (this.groupName === ServiceGroupNameEnum.SESSIONS && this.title) {
			return `${this.title} = ${this.getTotalPriceFormatted({currencyCode})}`
		}
		return this.breakdownFormatted(currencyCode, {
			mode: LineItemBreakdownMode.DEFAULT,
		})
	}
	breakdownFormattedSummaryCompact(currencyCode = this.currencyCode) {
		let childTitlesFormatted = this.formatSummaryChildTitles()
		if (childTitlesFormatted) {
			return `${this.getUsageValue()}${this.getUsageUnitsFormatted()} including ${childTitlesFormatted} = ${this.getTotalPriceFormatted({currencyCode})}`
		}
		if (!this.hasMultipleOwnUsageSummarySubItems()) {
			return this.breakdownFormatted(currencyCode, {
				mode: LineItemBreakdownMode.SUB_ITEMS_ONLY,
			})
		}
		return `${this.getUsageValue()}${this.getUsageUnitsFormatted()} at variable rate = ${this.getTotalPriceFormatted({currencyCode})}`
	}
	breakdownFormattedQuotePdf(currencyCode = this.currencyCode) {
		let breakdown = ""
		if (this.isUsageByPrice()) {
			let description = String(this.description || "").trim()
			if (description) {
				breakdown = description + this.breakdownFormattedSubLineItems(currencyCode)
			} else {
				breakdown = this.breakdownFormattedSubLineItems(currencyCode, {
					includeLeadingBreak: false,
				})
			}
		} else if (!this.hasSubItems() && !this.parentId) {
			breakdown = this.breakdownFormattedQuotePdfTopLevelNoChildren(currencyCode)
		} else {
			breakdown = this.breakdownFormattedGeneral(currencyCode)
		}
		return this.expandQuotePdfBreakSpacing(breakdown)
	}
	breakdownFormattedQuotePdfTopLevelNoChildren(currencyCode = this.currencyCode) {
		return `${this.getQuantityNumberFormatted()}${this.getQuantityFormatted()} x ${this.getItemPriceFormatted({currencyCode})}${this.getPriceUnitsFormatted()}`
	}
	expandQuotePdfBreakSpacing(text = "") {
		return String(text || "").replace(/<br\s*\/?>/g, "<br/><br/>")
	}
	breakdownFormattedGeneral(currencyCode = this.currencyCode) {
		return `${this.getQuantityNumberFormatted()}${this.getQuantityFormatted()} x ${this.getItemPriceFormatted({currencyCode})}${this.getPriceUnitsFormatted()} = ${this.getLineItemPriceFormatted(currencyCode)}`
			+ this.breakdownFormattedSubLineItems(currencyCode)
	}
	breakdownFormattedPiceUsage(currencyCode = this.currencyCode) {
		return `${this.getLineItemPriceFormatted(currencyCode)}${this.getPriceUnitsFormatted()}`
			+ this.breakdownFormattedSubLineItems(currencyCode)
	}

	isUsageExceeded() {
		return this.quantityMax > 0 && this.quantityTotalLogged > this.quantityMax;
	}

	isAmountExceeded() {
		return this.amountTotalLoggedCents > this.getApprovedAmountCents();
	}

	getLimitAndTotalLoggedFormatted({currencyCode = this.currencyCode} = {}) {
		// if (this.quantityMax && this.quantity) {
			let endValue = this.quantityTotalLogged;
			let unitsMax = this.getUsageUnitsMaxFormatted();
			if(this.isUsageByPrice()){
				let loggedPriceFormatted = currencyFormatted(currencyCode, endValue / 100, CURRENCY_FORMATTING_OPTIONS_SHORT);
				let totalPriceFormatted = currencyFormatted(currencyCode, this.quantityMax / 100, CURRENCY_FORMATTING_OPTIONS_SHORT);
				return `${loggedPriceFormatted} out of ${totalPriceFormatted} logged`;
			}
			return `${endValue} out of ${this.quantityMax}${unitsMax} logged`;
		// }
		// return "";
	}

	getAmountLimitAndTotalLoggedFormatted({currencyCode = this.currencyCode} = {}) {
		let loggedPriceFormatted = currencyFormatted(
			currencyCode,
			this.getActualAmountCents() / 100,
			CURRENCY_FORMATTING_OPTIONS_SHORT
		);
		let totalPriceFormatted = currencyFormatted(
			currencyCode,
			this.getApprovedAmountCents() / 100,
			CURRENCY_FORMATTING_OPTIONS_SHORT
		);
		return `total ${loggedPriceFormatted} out of ${totalPriceFormatted} logged`;
	}

	getActualAmountCents() {
		let sourceServiceId = this.sourceServiceId
		let invoiceLineItemsScope = this.getInvoiceLineItemsScope()
		if (!sourceServiceId || invoiceLineItemsScope.length === 0) {
			return this.getTotalPriceCents()
		}

		return invoiceLineItemsScope
			.filter(lineItem => lineItem?.sourceServiceId === sourceServiceId)
			.filter(lineItem => lineItem.getTotalPriceCents() > 0)
			.filter(lineItem => this.isLineItemAtOrBeforeCurrent(lineItem))
			.reduce((total, lineItem) => total + (lineItem.getTotalPriceCents() || 0), 0)
	}

	getInvoiceLineItemsScope() {
		return ensureArray(this._allInvoiceItemsRef)
			.flatMap(invoiceItem => invoiceItem?.lineItems || [])
			.filter(lineItem => !lineItem?.parentId)
	}

	isLineItemAtOrBeforeCurrent(lineItem) {
		let currentDateMs = Number(this.serviceDateMs) || 0
		let candidateDateMs = Number(lineItem?.serviceDateMs) || 0
		if (candidateDateMs !== currentDateMs) {
			return candidateDateMs < currentDateMs
		}
		return String(lineItem?.id || "") <= String(this.id || "")
	}

	getApprovedAmountCents() {
		let sourceServiceItem = this.getSourceQuoteServiceItem()
		if (sourceServiceItem) {
			return sourceServiceItem.getTotalPriceCents()
		}
		return this.getTotalPriceCents()
	}

	getSourceQuoteServiceItem() {
		let commissionDetails = this._commissionDetailsRef
		let sourceServiceId = this.sourceServiceId
		if (!commissionDetails || !sourceServiceId) {
			return null
		}
		return commissionDetails.getQuoteItem()?.getServiceItemById?.(sourceServiceId) || null
	}

	isUsageByPrice() {
		return this.serviceUsageType === ServiceUsageType.PRICE;
	}
	isUsageRate() {
		return this.serviceUsageType === ServiceUsageType.RATE;
	}

	isUsageByQuantity() {
		return this.serviceUsageType === ServiceUsageType.QUANTITY;
	}

	isUsageByQuantityNumber() {
		return this.serviceUsageType === ServiceUsageType.QUANTITY_NUMBER;
	}

	quantityMaxNormalized() {
		if(this.serviceUsageType === ServiceUsageType.PRICE) {
			return this.quantityMax / 100;
		}
		return this.quantityMax
	}

	// //option that shows range. A bit too tense cognitively since hour 2 to 5 logs 4 hours while intuitively 5-2 = 3 and not 4 which may bring unnecessary questioning.
	// getLimitAndTotalLoggedFormatted(divider = "") {
	// 	if (this.quantityMax && this.quantity) {
	// 		let startValue = this.quantityTotalLogged - this.quantity + 1
	// 		let endValue = this.quantityTotalLogged;
	// 		let endValueFormatted = this.quantity > 1 ? ` to ${endValue} inclusive` : "";
	// 		return `${this.quantityUnits} ${startValue}${endValueFormatted} of max ${this.quantityMax} ${this.quantityUnits ? ` ${pluralize(this.quantityUnits, this.quantityMax)}` : ""}${divider}`;
	// 	}
	// 	return "";
	// }

	breakdownFormattedSubLineItems(currencyCode = this.currencyCode, {
		includeLeadingBreak = true,
	} = {}) {
		let res = (this.subLineItems || [])
			.filter(lineItem => lineItem.getTotalPriceCents() > 0)
			.map((lineItem) => {
			let titleFormatted = lineItem.title ? lineItem.title + ": " : "";
			let lineItemBreakdownOptions = {
				mode: lineItem?._excludeParentLineInBreakdown
					? LineItemBreakdownMode.SUB_ITEMS_ONLY
					: LineItemBreakdownMode.DEFAULT,
			}
			return titleFormatted + lineItem.breakdownFormatted(currencyCode, lineItemBreakdownOptions)
		}).join("<br/>");
		if(res && includeLeadingBreak) {
			res = `<br/>${res}`;
		}
		return res;
	}

	breakdownFormattedNoQuantity(currencyCode = this.currencyCode) {
		return `${this.getQuantityUnitsFormatted()} x ${this.getItemPriceFormatted({currencyCode})}${this.getPriceUnitsFormatted()} = ${this.getTotalPriceFormatted({currencyCode})}`;
	}
	breakdownFormattedNoQuantityNumber(currencyCode = this.currencyCode) {
		return `${this.getQuantityNumberUnitsFormatted()} x ${this.quantity} ${this.breakdownFormattedNoQuantity(currencyCode)}`;
	}

	getQuantityNumberFormatted() {
		return this.quantityNumber !== 1
			? `${this.quantityNumber}${this.getQuantityNumberUnitsFormatted()} x `
			: "";
	}
	getQuantityNumberUnitsFormatted() {
		return this.quantityNumberUnits ? ` ${pluralize(this.quantityNumberUnits, this.quantityNumber)}` : ""
	}

	getQuantityFormatted() {
		return `${this.quantity}${this.getQuantityUnitsFormatted()}`;
	}
	getQuantityUnitsFormatted() {
		return this.quantityUnits ? ` ${pluralize(this.quantityUnits, this.quantity)}` : "";
	}

	getItemPriceFormatted({currencyCode = this.currencyCode, itemPrice = this.itemPrice} = {}) {
		return currencyFormatted(currencyCode, itemPrice, CURRENCY_FORMATTING_OPTIONS_SHORT);
	}

	getPriceUnitsFormatted() {
		return this.itemPriceUnits ? ` per ${this.itemPriceUnits}` : "";
	}

	// getUsageValue(){
	// 	return Number(this.quantityMax) || 0;
	// }

	setUsageValue(value){
		switch (this.serviceUsageType) {
			case ServiceUsageType.QUANTITY_NUMBER:
				this.quantityNumber = value;
				break
			case ServiceUsageType.QUANTITY:
				this.quantity = value;
				break
			case ServiceUsageType.PRICE:
				this.itemPriceCents = value;
				break
			default:
				this.quantityNumber = value;
				break
		}
	}

	getUsageValue(){
		let val
		switch (this.serviceUsageType) {
			case ServiceUsageType.QUANTITY_NUMBER:
				val = this.quantityNumber;
				break
			case ServiceUsageType.QUANTITY:
				val = this.quantity;
				break
			case ServiceUsageType.PRICE:
				val = this.itemPriceCents;
				break
			default:
				val = this.quantityNumber;
				break
		}
		return Number(val) || 0;
	}

	getUsageUnits(){
		switch (this.serviceUsageType) {
			case ServiceUsageType.QUANTITY_NUMBER:
				return this.quantityNumberUnits;
			case ServiceUsageType.QUANTITY:
				return this.quantityUnits;
			case ServiceUsageType.PRICE:
				return this.itemPriceUnits;
			default:
				return this.quantityNumberUnits;
		}
	}

	getUsageUnitsFormatted(){
		return this.getUsageUnits() ? ` ${pluralize(this.getUsageUnits(), this.getUsageValue())}` : "";
	}
	getUsageUnitsMaxFormatted(){
		return this.getUsageUnits() ? ` ${pluralize(this.getUsageUnits(), this.quantityMax)}` : "";
	}


	static fromSessionEntry({sessionEntry, quoteItem, sourceServiceId = null}) {
		if (!sessionEntry || !quoteItem) {
			return null;
		}
		let quotedService = quoteItem.getServiceItemById(sessionEntry.sourceServiceId || sourceServiceId)
		if (!quotedService) return null
		let serviceItem = classToClass(quotedService);
		let durationMinutes = sessionEntry.durationInMinutes()
		let duration = ["hour", "hours"].includes(serviceItem.quantityUnits)
			? durationMinutes / 60
			: durationMinutes

		console.log("LineItem.fromSessionEntry: serviceItem = ", serviceItem);

		let sessionLineItem = new LineItem({
			...(serviceItem || {}),
			id: sessionEntry.id,
			sourceServiceId: serviceItem?.id || null,
			sourceSessionEntryId: sessionEntry.id,
			groupName: ServiceGroupNameEnum.SESSIONS,
			title: `${sessionEntry.sessionNumber ? `Session ${sessionEntry.sessionNumber}` : "Session"} - ${sessionEntry.startTimeFormatted("DD MMM YYYY") || "Date not set"}`,
			quantityNumber: 1,
			quantity: duration,
			// quantityUnits: "min",
			// itemPrice: serviceItem?.value || 0,
			// itemPriceUnits: "hour",
			// conversionFactor: 1/60, // minutes to hours conversion,
			serviceDateMs: sessionEntry.startTimeMs,
		});
		LineItem.normalizeSessionDerivedSubItems(sessionLineItem);
		sessionLineItem.setDateIfNeeded(sessionEntry.startTimeMs);
		return sessionLineItem;
	}

	static normalizeSessionDerivedSubItems(parentLineItem) {
		for (let childLineItem of ensureArray(parentLineItem?.subLineItems)) {
			let childSourceServiceId = childLineItem.sourceServiceId || childLineItem.id || null
			childLineItem.sourceServiceId = childSourceServiceId
			childLineItem.id = `${parentLineItem.id}_${childSourceServiceId || "subItem"}`
			childLineItem.parentId = parentLineItem.id
			childLineItem.quantityNumber = 1
			LineItem.normalizeSessionDerivedSubItems(childLineItem)
		}
	}
}
